import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { subscribeToWaitlist } from "@/api/subscribe";

const Subscribe = () => {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email) {
      toast({
        title: "Campos incompletos",
        description: "Por favor, rellena todos los campos obligatorios.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      await subscribeToWaitlist({ name, email, phone });

      setSuccess(true);
      setName("");
      setEmail("");
      setPhone("");

      toast({
        title: "¡Registro exitoso!",
        description: "Gracias por unirte a nuestra lista de espera. Pronto recibirás noticias sobre Taxby.",
      });
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Hubo un problema al enviar el formulario. Inténtalo de nuevo más tarde.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="subscribe" className="section-padding bg-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white -z-10"></div>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-1/3 left-1/4 h-64 w-64 bg-taxby-teal/10 rounded-full animate-float" style={{ animationDelay: '0.3s' }}></div>
        <div className="absolute top-2/3 right-1/4 h-40 w-40 bg-taxby-purple/10 rounded-full animate-float" style={{ animationDelay: '0.7s' }}></div>
        <div className="absolute bottom-1/4 left-1/2 h-52 w-52 bg-taxby-orange/10 rounded-full animate-float" style={{ animationDelay: '1.1s' }}></div>
      </div>

      <div className="container mx-auto">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-5">
            <div className="md:col-span-2 bg-taxby-hero p-8 text-white flex flex-col justify-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-6 text-center md:text-left">Únete a la lista de espera</h2>
              <p className="mb-6">Sé de los primeros en disfrutar de Taxby y recibe ofertas especiales para early adopters.</p>
              <div className="hidden md:block">
                <ul className="space-y-4">
                  <li className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
                      <span className="text-lg font-bold">1</span>
                    </div>
                    <div>Regístrate hoy</div>
                  </li>
                  <li className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
                      <span className="text-lg font-bold">2</span>
                    </div>
                    <div>Recibe actualizaciones</div>
                  </li>
                  <li className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
                      <span className="text-lg font-bold">3</span>
                    </div>
                    <div>Acceso prioritario al lanzamiento</div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="md:col-span-3 p-8">
              {success ? (
                <div className="text-center py-12">
                  <svg className="h-16 w-16 text-taxby-purple mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-2xl font-bold text-taxby-violet mb-2">¡Gracias por registrarte!</h3>
                  <p className="text-gray-600 mb-6">Te mantendremos informado sobre el lanzamiento de Taxby.</p>
                  <Button
                    onClick={() => setSuccess(false)}
                    variant="outline"
                    className="border-taxby-purple text-taxby-purple hover:bg-taxby-purple/10"
                  >
                    Registrar otro correo
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-taxby-violet mb-6">Regístrate ahora</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre completo *
                      </label>
                      <Input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Tu nombre"
                        required
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Correo electrónico *
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@correo.com"
                        required
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Teléfono (opcional)
                      </label>
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Tu número de teléfono"
                        className="w-full"
                      />
                    </div>

                    <div className="pt-2">
                      <Button
                        type="submit"
                        className="w-full rounded-full bg-taxby-purple hover:bg-taxby-violet text-white"
                        disabled={loading}
                      >
                        {loading ? "Enviando..." : "Unirme a la lista de espera"}
                      </Button>
                    </div>

                    <p className="text-xs text-gray-500 mt-4">
                      Al registrarte, aceptas recibir comunicaciones relacionadas con Taxby.
                      Puedes darte de baja en cualquier momento.
                      No compartiremos tus datos con terceros.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Subscribe;
